import React, {useState} from "react";
import PropTypes from "prop-types";
import styles from "./apiTool.module.css";
import {API_LABELS} from "./endpoints.js";
import FieldTable, {FieldValue} from "./FieldTable.component.jsx";

const STATUS_CLASS = {
    "equal": styles.statusEqual,
    "equivalent": styles.statusEquivalent,
    "different": styles.statusDifferent,
    "only-task": styles.statusOnlyTask,
    "only-web": styles.statusOnlyWeb,
    "both-absent": styles.statusBothAbsent,
};

/*
 * These describe what compare.js#compareResults returns. The statuses are taken from STATUS_CLASS
 * above rather than listed again, so the two cannot drift apart.
 */
const STATUS = PropTypes.oneOf(Object.keys(STATUS_CLASS));

/** A field value is whatever the API returned - absent, null, empty, a scalar or an object. */
const VALUE = PropTypes.any;

const FIELD_SHAPE = PropTypes.shape({
    taskField: PropTypes.string,
    webField: PropTypes.string,
    note: PropTypes.string,
    taskValue: VALUE,
    webValue: VALUE,
    status: STATUS,
    hint: PropTypes.string,
});

const UNMAPPED_SHAPE = PropTypes.arrayOf(PropTypes.shape({
    field: PropTypes.string.isRequired,
    sample: VALUE,
}));

const COMPARISON_SHAPE = PropTypes.shape({
    key: PropTypes.shape({
        task: PropTypes.string.isRequired,
        web: PropTypes.string.isRequired,
    }).isRequired,
    counts: PropTypes.shape({
        task: PropTypes.number.isRequired,
        web: PropTypes.number.isRequired,
        paired: PropTypes.number.isRequired,
        onlyTask: PropTypes.number.isRequired,
        onlyWeb: PropTypes.number.isRequired,
    }).isRequired,
    onlyTaskKeys: PropTypes.array.isRequired,
    onlyWebKeys: PropTypes.array.isRequired,
    // one entry per configured field mapping, counting how each compared across every item
    fieldSummary: PropTypes.arrayOf(PropTypes.shape({
        taskField: PropTypes.string,
        webField: PropTypes.string,
        note: PropTypes.string,
        equal: PropTypes.number,
        equivalent: PropTypes.number,
        different: PropTypes.number,
        onlyTask: PropTypes.number,
        onlyWeb: PropTypes.number,
        bothAbsent: PropTypes.number,
        examples: PropTypes.arrayOf(PropTypes.shape({
            key: PropTypes.any,
            taskValue: VALUE,
            webValue: VALUE,
            status: STATUS,
            hint: PropTypes.string,
        })),
    })).isRequired,
    // one entry per item present on both sides
    rows: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.any,
        differing: PropTypes.number,
        equivalent: PropTypes.number,
        fields: PropTypes.arrayOf(FIELD_SHAPE).isRequired,
    })).isRequired,
    unmapped: PropTypes.shape({
        task: UNMAPPED_SHAPE.isRequired,
        web: UNMAPPED_SHAPE.isRequired,
    }).isRequired,
});

/** What ApiTool records for one run; `comparison` is absent unless both sides returned 200. */
const RUN_SHAPE = PropTypes.shape({
    task: PropTypes.object,
    web: PropTypes.object,
    comparison: COMPARISON_SHAPE,
});

function Status({status, hint}) {
    return (
        <td className={`${styles.status} ${STATUS_CLASS[status] ?? ""}`}>
            {status}
            {hint && <span className={styles.muted}> ({hint})</span>}
        </td>
    );
}

Status.propTypes = {
    status: STATUS.isRequired,
    hint: PropTypes.string,
};

function unmappedCount(comparison) {
    return comparison.unmapped.task.length + comparison.unmapped.web.length;
}

/** compare.js#buildFieldMap keeps one entry per task/web pair, so the pair identifies a row. */
function fieldKey(field) {
    return `${field.taskField ?? ""} ${field.webField ?? ""}`;
}

function FieldSummaryTab({comparison}) {
    const {counts, fieldSummary} = comparison;
    const withDiff = fieldSummary.filter(field =>
        field.different || field.onlyTask || field.onlyWeb || field.equivalent);

    return (
        <>
            <div className={styles.counts}>
                <div><b>{counts.task}</b> Task API items</div>
                <div><b>{counts.web}</b> Web API items</div>
                <div><b>{counts.paired}</b> paired on <code>{comparison.key.task}</code></div>
                <div><b>{counts.onlyTask}</b> only in Task API</div>
                <div><b>{counts.onlyWeb}</b> only in Web API</div>
            </div>

            {(counts.onlyTask > 0 || counts.onlyWeb > 0) &&
                <p className={styles.muted}>
                    Only Task API: {comparison.onlyTaskKeys.join(", ") || "–"}<br/>
                    Only Web API: {comparison.onlyWebKeys.join(", ") || "–"}
                </p>
            }

            <p className={styles.muted}>
                {withDiff.length} of {fieldSummary.length} mapped fields disagree on at least one item.
                {unmappedCount(comparison) > 0 && <>
                    {" "}Another {unmappedCount(comparison)} field
                    {unmappedCount(comparison) === 1 ? " is" : "s are"} returned by only one API, so
                    {unmappedCount(comparison) === 1 ? " it pairs" : " they pair"} with nothing and
                    {unmappedCount(comparison) === 1 ? " is" : " are"} listed under <b>Unmapped fields</b>.
                </>}
            </p>

            <table>
                <thead>
                    <tr>
                        <th>Task API field</th>
                        <th>Web API field</th>
                        <th>equal</th>
                        <th>equivalent</th>
                        <th>different</th>
                        <th>only Task</th>
                        <th>only Web</th>
                        <th>both empty</th>
                        <th>Example</th>
                    </tr>
                </thead>
                <tbody>
                    {fieldSummary.map(field => (
                        <tr key={fieldKey(field)}>
                            <td>
                                <code>{field.taskField}</code>
                                {field.note && <><br/><span className={styles.muted}>{field.note}</span></>}
                            </td>
                            <td><code>{field.webField}</code></td>
                            <td className={styles.statusEqual}>{field.equal || ""}</td>
                            <td className={styles.statusEquivalent}>{field.equivalent || ""}</td>
                            <td className={styles.statusDifferent}>{field.different || ""}</td>
                            <td className={styles.statusOnlyTask}>{field.onlyTask || ""}</td>
                            <td className={styles.statusOnlyWeb}>{field.onlyWeb || ""}</td>
                            <td className={styles.muted}>{field.bothAbsent || ""}</td>
                            <td className={styles.val}>
                                {field.examples.map(example => (
                                    // at most one example per item, so the item's key identifies it
                                    <div key={example.key}>
                                        <FieldValue value={example.taskValue}/>
                                        <span className={styles.muted}> vs </span>
                                        <FieldValue value={example.webValue}/>
                                    </div>
                                ))}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );
}

FieldSummaryTab.propTypes = {
    comparison: COMPARISON_SHAPE.isRequired,
};

function RowDetail({row, onlyDifferences}) {
    const fields = onlyDifferences
        ? row.fields.filter(field => field.status !== "equal" && field.status !== "both-absent")
        : row.fields;

    if (fields.length === 0) {
        return <p className={styles.muted}>Every mapped field matches.</p>;
    }

    return (
        <table>
            <thead>
                <tr>
                    <th>Task API field</th>
                    <th>value</th>
                    <th>Web API field</th>
                    <th>value</th>
                    <th>status</th>
                </tr>
            </thead>
            <tbody>
                {fields.map(field => (
                    <tr key={fieldKey(field)}>
                        <td><code>{field.taskField}</code></td>
                        <td className={styles.val}><FieldValue value={field.taskValue}/></td>
                        <td><code>{field.webField}</code></td>
                        <td className={styles.val}><FieldValue value={field.webValue}/></td>
                        <Status status={field.status} hint={field.hint}/>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

RowDetail.propTypes = {
    row: PropTypes.shape({
        fields: PropTypes.arrayOf(FIELD_SHAPE).isRequired,
    }).isRequired,
    onlyDifferences: PropTypes.bool.isRequired,
};

function PerItemTab({comparison}) {
    const [onlyDifferences, setOnlyDifferences] = useState(true);
    const [expandedRow, setExpandedRow] = useState(null);
    const rows = comparison.rows;

    return (
        <>
            <div className={styles.filters}>
                <label className={styles.check}>
                    <input
                        type="checkbox"
                        checked={onlyDifferences}
                        onChange={event => setOnlyDifferences(event.target.checked)}
                    />
                    {" "}hide fields that match exactly
                </label>
                <span className={styles.muted}>
                    {rows.filter(row => row.differing).length} of {rows.length} items have at least one real difference
                </span>
            </div>

            <table>
                <thead>
                    <tr>
                        <th>{comparison.key.task}</th>
                        <th>Differences</th>
                        <th>Equivalent</th>
                        <th/>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, index) => (
                        <React.Fragment key={row.key}>
                            <tr
                                className={styles.clickable}
                                onClick={() => setExpandedRow(expandedRow === index ? null : index)}
                            >
                                <td><code>{row.key}</code></td>
                                <td className={`${styles.status} ${row.differing ? styles.statusDifferent : styles.statusEqual}`}>
                                    {row.differing}
                                </td>
                                <td className={`${styles.status} ${styles.statusEquivalent}`}>
                                    {row.equivalent || ""}
                                </td>
                                <td className={styles.muted}>{expandedRow === index ? "hide" : "show fields"}</td>
                            </tr>
                            {expandedRow === index &&
                                <tr>
                                    <td colSpan={4}>
                                        <RowDetail row={row} onlyDifferences={onlyDifferences}/>
                                    </td>
                                </tr>
                            }
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
        </>
    );
}

PerItemTab.propTypes = {
    comparison: COMPARISON_SHAPE.isRequired,
};

function UnmappedList({title, entries}) {
    return (
        <div>
            <h2>{title} <span>({entries.length})</span></h2>
            {entries.length === 0
                ? <p className={styles.muted}>None.</p>
                : <FieldTable
                    valueHeader="First non-empty value"
                    rows={entries.map(entry => ({field: entry.field, value: entry.sample}))}
                />
            }
        </div>
    );
}

UnmappedList.propTypes = {
    title: PropTypes.string.isRequired,
    entries: UNMAPPED_SHAPE.isRequired,
};

function UnmappedTab({comparison}) {
    return (
        <>
            <p className={styles.muted}>
                Fields returned by one API with no counterpart configured in the field map
                (see the operation&apos;s <code>compare</code> block under <code>operations/</code>).
            </p>
            <div className={styles.rawGrid}>
                <UnmappedList title="Task API only" entries={comparison.unmapped.task}/>
                <UnmappedList title="Web API only" entries={comparison.unmapped.web}/>
            </div>
        </>
    );
}

UnmappedTab.propTypes = {
    comparison: COMPARISON_SHAPE.isRequired,
};

function RawTab({run}) {
    return (
        <div className={styles.rawGrid}>
            <div>
                <h2>{API_LABELS.task}</h2>
                <pre>{JSON.stringify(run.task.body, null, 2)}</pre>
            </div>
            <div>
                <h2>{API_LABELS.web}</h2>
                <pre>{JSON.stringify(run.web.body, null, 2)}</pre>
            </div>
        </div>
    );
}

RawTab.propTypes = {
    run: PropTypes.shape({
        task: PropTypes.object.isRequired,
        web: PropTypes.object.isRequired,
    }).isRequired,
};

export default function Comparison({run}) {
    const [tab, setTab] = useState("summary");
    const comparison = run.comparison;

    // the counts are on the tabs because a field with no counterpart is otherwise easy to miss:
    // it is absent from both the summary and the per-item view by design
    const tabs = [
        {id: "summary", label: `Field summary (${comparison.fieldSummary.length})`},
        {id: "items", label: `Per item (${comparison.rows.length})`},
        {id: "unmapped", label: `Unmapped fields (${unmappedCount(comparison)})`},
        {id: "raw", label: "Raw responses"},
    ];

    return (
        <section className={styles.panel}>
            <nav className={styles.tabs}>
                {tabs.map(entry => (
                    <button
                        key={entry.id}
                        className={tab === entry.id ? styles.active : undefined}
                        onClick={() => setTab(entry.id)}
                    >
                        {entry.label}
                    </button>
                ))}
            </nav>

            {tab === "summary" && <FieldSummaryTab comparison={comparison}/>}
            {tab === "items" && <PerItemTab comparison={comparison}/>}
            {tab === "unmapped" && <UnmappedTab comparison={comparison}/>}
            {tab === "raw" && <RawTab run={run}/>}
        </section>
    );
}

Comparison.propTypes = {
    run: RUN_SHAPE.isRequired,
};
